/**
 * A FIFO concurrency limiter. At most `cap` tasks run at once; the rest wait in
 * arrival order and start as slots free. `release()` hands a finishing task's
 * slot straight to the next waiter (it does NOT re-increment the count), so the
 * active count stays exact. Create one instance per shared limit.
 *
 * Extracted from the receive-side auto-download queue so the send-side blob queue
 * can reuse the exact same battle-tested accounting.
 */
export interface FifoSemaphore {
  /** Run `task` under the cap; the slot is released even if it throws/aborts. */
  run<T>(task: () => Promise<T>): Promise<T>;
  /** Test-only: reset counters between cases. */
  reset(): void;
  /** Test-only: current in-flight count. */
  active(): number;
}

export function createFifoSemaphore(cap: number): FifoSemaphore {
  let active = 0;
  const waiters: Array<() => void> = [];

  function acquire(): Promise<void> {
    if (active < cap) {
      active++;
      return Promise.resolve();
    }
    // No free slot — queue until release() hands one over. release() does NOT
    // increment `active` when it wakes a waiter: the finishing task's slot is
    // transferred straight to the waiter, so the count stays exact.
    return new Promise<void>((resolve) => waiters.push(resolve));
  }

  function release(): void {
    const next = waiters.shift();
    if (next) {
      next();
    } else {
      active -= 1;
    }
  }

  return {
    async run<T>(task: () => Promise<T>): Promise<T> {
      await acquire();
      try {
        return await task();
      } finally {
        release();
      }
    },
    reset() {
      active = 0;
      waiters.length = 0;
    },
    active() {
      return active;
    },
  };
}
